import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { fetcher } from '@/utils/fetcher';
import { nameSchema } from '@/utils/schemas';

export const collectionScheme = z.object({
  Content: z.string(),
  FilePath: z.string(),
  FromID: z.number(),
  FromName: nameSchema,
  FromType: z.enum(['friend', 'group']),
  ID: z.number(),
  SmallPicturePath: z.string(),
  TimeStamp: z.number().transform((v) => v * 1000),
  Type: z.nativeEnum(ConversationMessageContentType),
  UserID: z.number(),
});

export type Collection = z.infer<typeof collectionScheme>;

export const collectionListQueryOptions = queryOptions({
  queryKey: ['/GetCollects'],
  queryFn: ({ signal }) =>
    fetcher<ListRespondedWith<Collection>>('/GetCollects', undefined, {
      signal,
      schema: generateListResponseScheme(collectionScheme),
      validResponseCodes: [0, 1],
    }).then((response) => response?.Data ?? []),
  placeholderData: [] as Collection[],
  staleTime: 60 * 60 * 1000,
});

export default function useCollections() {
  const query = useQuery(collectionListQueryOptions);

  return {
    collections: query.data ?? [],
    ...query,
  };
}
